import {
  _maxPortfolioLimit,
  _verifiedUserMaxPortfolioLimit,
} from '../portfolio-settings';

/**
 * The error code that is returned when the user has reached the portfolio limit
 * @constant
 * @type {string}
 */
export const portfolioLimitReachedError = 'portfolio_limit_reached';

/**
 * The error code that is returned when the portfolio name is already used
 * @constant
 * @type {string}
 */
export const portfolioUniqueNameError = 'portfolio_name_already_used';

/**
 * The error code that is returned when the user has not confirmed their email
 * @constant
 * @type {string}
 */
export const emailNotConfirmedError =
  'create_portfolio_disabled_mail_validation';

/**
 * Returns the reason why the user cannot create a portfolio
 * @param emailConfirmed Whether the user has confirmed their email
 * @param ownedPortfolios The number of portfolios the user owns
 * @param isVerified Whether the user is verified
 * @returns The reason why the user cannot create a portfolio
 */
export function cannotCreatePortfolioReason(
  emailConfirmed: boolean,
  ownedPortfolios: number | undefined,
  isVerified: boolean
) {
  if (!emailConfirmed) return emailNotConfirmedError;
  if (portfolioLimitReached(ownedPortfolios, isVerified))
    return portfolioLimitReachedError;
  return '';
}

/**
 * Returns whether the user can create a portfolio
 * @param emailConfirmed Whether the user has confirmed their email
 * @param ownedPortfolios The number of portfolios the user owns
 * @param isVerified Whether the user is verified
 * @returns Whether the user can create a portfolio
 */
export function canCreatePortfolio(
  emailConfirmed: boolean,
  ownedPortfolios: number | undefined,
  isVerified: boolean
) {
  return emailConfirmed && !portfolioLimitReached(ownedPortfolios, isVerified);
}

/**
 * Returns whether the user has reached the portfolio limit
 * @param ownedPortfolios The number of portfolios the user owns
 * @param isVerified Whether the user is verified
 * @returns Whether the user has reached the portfolio limit
 */
export function portfolioLimitReached(
  ownedPortfolios: number | undefined,
  isVerified: boolean
) {
  if (isVerified) {
    return ownedPortfolios && ownedPortfolios >= _verifiedUserMaxPortfolioLimit;
  } else {
    return ownedPortfolios && ownedPortfolios >= _maxPortfolioLimit;
  }
}

/**
 * Returns the maximum portfolio limit for free users
 */
export const maxPortfoliosPerFreeUser = _maxPortfolioLimit;

/**
 * Returns the maximum portfolio limit for verified users
 */
export const verifiedUserMaxPortfoliosPerFreeUser =
  _verifiedUserMaxPortfolioLimit;
